"use client";

import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Logo } from "@/components/navigation/Logo";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { navigation, primaryCta } from "@/config/navigation";
import { cn } from "@/lib/utils";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const solid = scrolled || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        solid
          ? "border-b border-ink-200/70 bg-white/90 backdrop-blur-lg"
          : "border-b border-transparent bg-transparent",
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-4 sm:h-18">
        <a
          href="#top"
          aria-label="aComputer – úvod"
          className="shrink-0 rounded-lg"
          onClick={() => setOpen(false)}
        >
          <Logo tone={solid ? "dark" : "light"} />
        </a>

        <nav aria-label="Hlavní navigace" className="hidden lg:block">
          <ul className="flex items-center gap-7">
            {navigation.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className={cn(
                    "rounded-md py-2 text-sm font-semibold transition-colors",
                    solid
                      ? "text-ink-700 hover:text-brand-600"
                      : "text-ink-200 hover:text-white",
                  )}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden lg:block">
          <Button href={primaryCta.href} size="md">
            {primaryCta.label}
          </Button>
        </div>

        <button
          type="button"
          className={cn(
            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors lg:hidden",
            solid
              ? "border-ink-200 bg-white text-ink-900 hover:border-brand-300"
              : "border-white/20 bg-white/10 text-white hover:bg-white/20",
          )}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Zavřít menu" : "Otevřít menu"}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>

      <div
        id="mobile-menu"
        hidden={!open}
        className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-ink-200 bg-white lg:hidden"
      >
        <Container className="flex flex-col gap-1 py-5">
          {navigation.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className={cn(
                "rounded-xl px-3 py-3 text-base font-semibold text-ink-800 transition-colors",
                "hover:bg-ink-50 hover:text-brand-600",
              )}
            >
              {item.label}
            </a>
          ))}
          <Button
            href={primaryCta.href}
            className="mt-3 w-full"
            onClick={() => setOpen(false)}
          >
            {primaryCta.label}
          </Button>
        </Container>
      </div>
    </header>
  );
}
